import type { ConnectorSide } from "./canvas-object";

// コネクタ（接続矢印）の幾何計算。図形と図形を直交折れ線で結ぶための純粋関数群。
// クライアント描画（connector-object.tsx / オーバーレイ）と PDF / サムネイル出力に加え、
// サーバ側の参照整合（保存 bbox の再計算）でも共用するため domain に置く。

export interface ConnectorFrame {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface ConnectorPoint {
  readonly x: number;
  readonly y: number;
}

// 端点から外向きに伸ばす助走（スタブ）の長さ。矢印が図形へ垂直に出入りするように見せる。
export const CONNECTOR_STUB_PX = 24;

// コネクタ保存 bbox に与える余白（px）。矢じり・線幅の張り出しを含め、かつ水平/垂直コネクタで
// w/h が 0 になって範囲選択・他参加者選択枠が潰れるのを防ぐ。描画は端点から都度計算するため、
// この保存 bbox は AABB を参照する箇所（範囲選択・ミニマップ・他参加者選択枠・カリング）専用。
export const CONNECTOR_BBOX_PAD_PX = 12;

// 端点が同一座標に潰れたときに連続点を間引くための許容誤差。
const EPSILON = 0.01;

/**
 * オブジェクト外接矩形の指定辺の中点（接続アンカー）をステージ座標で返す。
 */
export function sideAnchor(frame: ConnectorFrame, side: ConnectorSide): ConnectorPoint {
  switch (side) {
    case "top":
      return { x: frame.x + frame.w / 2, y: frame.y };
    case "bottom":
      return { x: frame.x + frame.w / 2, y: frame.y + frame.h };
    case "left":
      return { x: frame.x, y: frame.y + frame.h / 2 };
    case "right":
      return { x: frame.x + frame.w, y: frame.y + frame.h / 2 };
  }
}

/**
 * 指定辺の外向き単位ベクトルを返す（top は上向き= y- 方向）。
 */
export function sideDirection(side: ConnectorSide): ConnectorPoint {
  switch (side) {
    case "top":
      return { x: 0, y: -1 };
    case "bottom":
      return { x: 0, y: 1 };
    case "left":
      return { x: -1, y: 0 };
    case "right":
      return { x: 1, y: 0 };
  }
}

/**
 * 指定点に最も近い辺を返す。接続先のスナップ辺の決定に使う。
 * 4 辺のアンカーのうち、点との距離が最小の辺を選ぶ。
 */
export function nearestSide(frame: ConnectorFrame, point: ConnectorPoint): ConnectorSide {
  const sides: readonly ConnectorSide[] = ["top", "right", "bottom", "left"];
  let best: ConnectorSide = "top";
  let bestDist = Infinity;
  for (const side of sides) {
    const anchor = sideAnchor(frame, side);
    const dist = (anchor.x - point.x) ** 2 + (anchor.y - point.y) ** 2;
    if (dist < bestDist) {
      bestDist = dist;
      best = side;
    }
  }
  return best;
}

/**
 * 端点 p から外向き dir へ伸ばすスタブ端点を返す。
 * 接続相手 other が外向き方向にある（向かい合う）場合は、相手との距離の半分を上限にスタブ長を
 * 抑える。これにより図形間隔が短い（< 2 * STUB）ときにスタブが相手側へ突き抜けて、
 * 経路が図形内部を通る不具合を防ぐ。相手が逆方向（背を向け合う / 重なり）にあるときは
 * 迂回が必要なため、規定のスタブ長を確保する。
 */
function stubPoint(p: ConnectorPoint, dir: ConnectorPoint, other: ConnectorPoint): ConnectorPoint {
  if (dir.x !== 0) {
    const toward = (other.x - p.x) * dir.x;
    const ext = toward > 0 ? Math.min(CONNECTOR_STUB_PX, toward / 2) : CONNECTOR_STUB_PX;
    return { x: p.x + dir.x * ext, y: p.y };
  }
  const toward = (other.y - p.y) * dir.y;
  const ext = toward > 0 ? Math.min(CONNECTOR_STUB_PX, toward / 2) : CONNECTOR_STUB_PX;
  return { x: p.x, y: p.y + dir.y * ext };
}

/**
 * 2 つのスタブ端点を直交（マンハッタン）折れ線でつなぐ中間点を返す。
 * 端点の向き（水平 / 垂直）の組み合わせに応じて、L 字または Z 字の中継点を生成する。
 */
function orthogonalMidpoints(
  p1: ConnectorPoint,
  dir1: ConnectorPoint,
  p2: ConnectorPoint,
  dir2: ConnectorPoint
): ConnectorPoint[] {
  const horiz1 = dir1.x !== 0;
  const horiz2 = dir2.x !== 0;
  if (horiz1 && horiz2) {
    // 両端とも水平: 中央の縦線で結ぶ（Z 字）。
    const midX = (p1.x + p2.x) / 2;
    return [
      { x: midX, y: p1.y },
      { x: midX, y: p2.y },
    ];
  }
  if (!horiz1 && !horiz2) {
    // 両端とも垂直: 中央の横線で結ぶ（Z 字）。
    const midY = (p1.y + p2.y) / 2;
    return [
      { x: p1.x, y: midY },
      { x: p2.x, y: midY },
    ];
  }
  // 片方が水平・片方が垂直: 単一の角で結ぶ（L 字）。
  if (horiz1) {
    return [{ x: p2.x, y: p1.y }];
  }
  return [{ x: p1.x, y: p2.y }];
}

/**
 * 2 つの図形フレームの指定辺どうしを直交折れ線で結ぶルート点列（ステージ座標）を返す。
 * 先頭が from アンカー、末尾が to アンカー。連続する重複点は除去する。
 */
export function computeConnectorRoute(
  fromFrame: ConnectorFrame,
  fromSide: ConnectorSide,
  toFrame: ConnectorFrame,
  toSide: ConnectorSide
): ConnectorPoint[] {
  const a = sideAnchor(fromFrame, fromSide);
  const b = sideAnchor(toFrame, toSide);
  const da = sideDirection(fromSide);
  const db = sideDirection(toSide);
  // スタブは相手方向では距離の半分を上限に抑える（近接時に図形を貫通しないように）。
  const a1 = stubPoint(a, da, b);
  const b1 = stubPoint(b, db, a);
  const mids = orthogonalMidpoints(a1, da, b1, db);
  return dedupePoints([a, a1, ...mids, b1, b]);
}

/**
 * 連続する（ほぼ）同一座標の点を 1 つに畳む。
 */
export function dedupePoints(points: readonly ConnectorPoint[]): ConnectorPoint[] {
  const result: ConnectorPoint[] = [];
  for (const point of points) {
    const prev = result[result.length - 1];
    if (prev && Math.abs(prev.x - point.x) < EPSILON && Math.abs(prev.y - point.y) < EPSILON) {
      continue;
    }
    result.push(point);
  }
  return result;
}

/**
 * 点列の外接矩形を返す。描画キャンバスのサイズ算出や保存 bbox の素として使う。
 */
export function routeBoundingBox(points: readonly ConnectorPoint[]): ConnectorFrame {
  if (points.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    if (point.x < minX) minX = point.x;
    if (point.y < minY) minY = point.y;
    if (point.x > maxX) maxX = point.x;
    if (point.y > maxY) maxY = point.y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/**
 * コネクタの保存用フレーム（x/y/w/h）を返す。ルート外接矩形に矢じり・線幅ぶんの余白を加える。
 * 作成時・端点移動時の保存値を一元化し、AABB 参照箇所で見た目とずれにくくする。
 */
export function connectorStoredFrame(
  fromFrame: ConnectorFrame,
  fromSide: ConnectorSide,
  toFrame: ConnectorFrame,
  toSide: ConnectorSide
): ConnectorFrame {
  const bbox = routeBoundingBox(computeConnectorRoute(fromFrame, fromSide, toFrame, toSide));
  return {
    x: bbox.x - CONNECTOR_BBOX_PAD_PX,
    y: bbox.y - CONNECTOR_BBOX_PAD_PX,
    w: bbox.w + CONNECTOR_BBOX_PAD_PX * 2,
    h: bbox.h + CONNECTOR_BBOX_PAD_PX * 2,
  };
}
